import React, { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import SEO from "./SEO";
import SportPicker from "./scoring/SportPicker";
import ScenarioPicker from "./scoring/ScenarioPicker";
import TipPreviewCard from "./scoring/TipPreviewCard";
import ActualResultCard from "./scoring/ActualResultCard";
import PointsDisplay from "./scoring/PointsDisplay";
import FirstLegInput from "./scoring/FirstLegInput";
import { calculatePoints, getPointsReason } from "../utils/scoringCalculator";
import "../styles/Scoring.css";

const TEAM_COLORS = {
  FOOTBALL: { home: "#002855", away: "#b91c1c" },
  HOCKEY:   { home: "#4DA8DA", away: "#b91c1c" },
};

// Hockey golden-goal rule: when overtime is on, the final goal difference
// must be exactly 1. This snaps an invalid pair back to a valid one,
// preserving the user's just-edited side.
function clampHockeyOT(home, away, changedSide) {
  if (Math.abs(home - away) === 1) return { home, away };
  if (changedSide === "home") {
    if (home - away > 0) return { home, away: Math.max(0, home - 1) };
    return { home, away: home + 1 };
  }
  if (away - home > 0) return { home: Math.max(0, away - 1), away };
  return { home: away + 1, away };
}

const SPORT_ACCENT = {
  FOOTBALL: ["#003a7e", "#002855"],
  HOCKEY:   ["#7ec6f0", "#4DA8DA"],
};

const fadeSlide = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  exit:    { opacity: 0, y: -8 },
  transition: { duration: 0.25, ease: "easeOut" },
};

const Scoring = () => {
  const { t } = useTranslation();

  const [sport, setSport] = useState(null);
  const [scenario, setScenario] = useState(null);

  const [predHome, setPredHome] = useState(2);
  const [predAway, setPredAway] = useState(1);
  const [actualHome, setActualHome] = useState(2);
  const [actualAway, setActualAway] = useState(1);

  const [predOvertime, setPredOvertime] = useState(false);
  const [actualOvertime, setActualOvertime] = useState(false);
  const [predAdvancing, setPredAdvancing] = useState("HOME");
  const [actualAdvancing, setActualAdvancing] = useState("HOME");

  const [firstLegHome, setFirstLegHome] = useState(1);
  const [firstLegAway, setFirstLegAway] = useState(0);

  const handleSportChange = (next) => {
    setSport(next);
    if (next === "HOCKEY") {
      setScenario("STANDARD");
    } else {
      setScenario(null);
    }
  };

  // Score-change handlers — for hockey + overtime ON, snap to a valid
  // golden-goal scoreline (diff exactly 1) and adjust the opposite side.
  const makeScoreHandler = (side, currentOther, overtime, setHome, setAway) => (value) => {
    if (sport === "HOCKEY" && overtime) {
      const pair = side === "home"
        ? clampHockeyOT(value, currentOther, "home")
        : clampHockeyOT(currentOther, value, "away");
      setHome(pair.home);
      setAway(pair.away);
    } else if (side === "home") {
      setHome(value);
    } else {
      setAway(value);
    }
  };

  const handlePredHome    = makeScoreHandler("home", predAway,   predOvertime,   setPredHome,   setPredAway);
  const handlePredAway    = makeScoreHandler("away", predHome,   predOvertime,   setPredHome,   setPredAway);
  const handleActualHome  = makeScoreHandler("home", actualAway, actualOvertime, setActualHome, setActualAway);
  const handleActualAway  = makeScoreHandler("away", actualHome, actualOvertime, setActualHome, setActualAway);

  const handlePredOvertime = (next) => {
    setPredOvertime(next);
    if (next && sport === "HOCKEY") {
      const pair = clampHockeyOT(predHome, predAway, "home");
      setPredHome(pair.home);
      setPredAway(pair.away);
    }
  };

  const handleActualOvertime = (next) => {
    setActualOvertime(next);
    if (next && sport === "HOCKEY") {
      const pair = clampHockeyOT(actualHome, actualAway, "home");
      setActualHome(pair.home);
      setActualAway(pair.away);
    }
  };

  const testerVisible = sport === "HOCKEY" || (sport === "FOOTBALL" && scenario !== null);
  const effectiveScenario = scenario || "STANDARD";
  const isSecondLeg = effectiveScenario === "SECOND_LEG";
  const isFootballKo = sport === "FOOTBALL" && effectiveScenario === "KO";

  const colors = TEAM_COLORS[sport] || TEAM_COLORS.FOOTBALL;
  const accent = SPORT_ACCENT[sport] || SPORT_ACCENT.FOOTBALL;

  // For SECOND_LEG, "overtime" = aggregate draw after both legs. Derive it instead of
  // exposing a manual toggle, so the calculator gets the right input automatically.
  const tipAggregate = isSecondLeg
    ? { home: firstLegHome + predHome,   away: firstLegAway + predAway }
    : null;
  const actualAggregate = isSecondLeg
    ? { home: firstLegHome + actualHome, away: firstLegAway + actualAway }
    : null;
  const tipAggregateInfo    = tipAggregate    ? { ...tipAggregate,    draw: tipAggregate.home    === tipAggregate.away }    : null;
  const actualAggregateInfo = actualAggregate ? { ...actualAggregate, draw: actualAggregate.home === actualAggregate.away } : null;

  // For a single football knockout match, "overtime" = a draw after 90 minutes.
  // Derive it from the score like the real match card does (no manual toggle).
  // Hockey keeps its explicit toggle (golden goal makes the final score a win).
  const effectivePredOvertime = isSecondLeg
    ? !!tipAggregateInfo?.draw
    : isFootballKo
      ? predHome === predAway
      : predOvertime;
  const effectiveActualOvertime = isSecondLeg
    ? !!actualAggregateInfo?.draw
    : isFootballKo
      ? actualHome === actualAway
      : actualOvertime;

  const points = useMemo(() => {
    if (!testerVisible) return 0;
    return calculatePoints({
      sport,
      scenario: effectiveScenario,
      actualHome, actualAway, predHome, predAway,
      actualOvertime: effectiveActualOvertime,
      predOvertime: effectivePredOvertime,
      actualAdvancing, predAdvancing,
    });
  }, [
    testerVisible, sport, effectiveScenario,
    actualHome, actualAway, predHome, predAway,
    effectiveActualOvertime, effectivePredOvertime,
    actualAdvancing, predAdvancing,
  ]);

  const reason = useMemo(() => {
    if (!testerVisible) return null;
    return getPointsReason(
      {
        sport,
        scenario: effectiveScenario,
        predHome, predAway, actualHome, actualAway,
        actualOvertime: effectiveActualOvertime,
        predOvertime: effectivePredOvertime,
      },
      points
    );
  }, [
    testerVisible, sport, effectiveScenario,
    predHome, predAway, actualHome, actualAway,
    effectiveActualOvertime, effectivePredOvertime, points,
  ]);

  return (
    <div className="scoring-page">
      <SEO
        title={t("scoring.seo.title")}
        description={t("scoring.seo.description")}
      />

      <header className="scoring-hero">
        <h1 className="scoring-hero__title">
          <span role="img" aria-label="calc">🧮</span>{" "}
          {t("scoring.hero.title")}
        </h1>
        <p className="scoring-hero__subtitle">{t("scoring.hero.subtitle")}</p>
      </header>

      <section className="scoring-step">
        <div className="scoring-step__label">
          <span className="scoring-step__num scoring-step__num--neutral">1</span>
          {t("scoring.step.sport")}
        </div>
        <SportPicker value={sport} onChange={handleSportChange} />
      </section>

      <AnimatePresence>
        {sport === "FOOTBALL" && (
          <motion.section
            key="scenario-step"
            className="scoring-step"
            {...fadeSlide}
          >
            <div className="scoring-step__label">
              <span
                className="scoring-step__num"
                style={{ background: `linear-gradient(135deg, ${accent[0]}, ${accent[1]})` }}
              >
                2
              </span>
              {t("scoring.step.scenario")}
            </div>
            <ScenarioPicker value={scenario} onChange={setScenario} accent={accent} />
          </motion.section>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {testerVisible && (
          <motion.section
            key="tester-step"
            className="scoring-step scoring-step--tester"
            {...fadeSlide}
          >
            <div className="scoring-step__label">
              <span
                className="scoring-step__num"
                style={{ background: `linear-gradient(135deg, ${accent[0]}, ${accent[1]})` }}
              >
                {sport === "FOOTBALL" ? 3 : 2}
              </span>
              {t("scoring.step.try")}
            </div>

            {isSecondLeg && (
              <FirstLegInput
                home={firstLegHome}
                away={firstLegAway}
                onHome={setFirstLegHome}
                onAway={setFirstLegAway}
                homeColor={colors.home}
                awayColor={colors.away}
              />
            )}

            <div className="scoring-tester-grid">
              <div className="scoring-tester-col">
                <TipPreviewCard
                  sport={sport}
                  scenario={effectiveScenario}
                  homeScore={predHome}
                  awayScore={predAway}
                  onHome={setPredHome}
                  onAway={setPredAway}
                  overtime={effectivePredOvertime}
                  onOvertime={setPredOvertime}
                  advancing={predAdvancing}
                  onAdvancing={setPredAdvancing}
                  homeColor={colors.home}
                  awayColor={colors.away}
                  aggregate={tipAggregateInfo}
                  accent={accent}
                />
              </div>

              <div className="scoring-tester-col">
                <ActualResultCard
                  sport={sport}
                  scenario={effectiveScenario}
                  homeScore={actualHome}
                  awayScore={actualAway}
                  onHome={setActualHome}
                  onAway={setActualAway}
                  overtime={effectiveActualOvertime}
                  onOvertime={setActualOvertime}
                  advancing={actualAdvancing}
                  onAdvancing={setActualAdvancing}
                  homeColor={colors.home}
                  awayColor={colors.away}
                  aggregate={actualAggregateInfo}
                />
              </div>

              <div className="scoring-tester-col scoring-tester-col--points">
                <PointsDisplay points={points} reason={reason} />
              </div>
            </div>
          </motion.section>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Scoring;
